import { useCallback, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { toast } from "@jsq/ds/core";
import {
  GetFormulasDocument,
  GetQueriesDocument,
  GetReportTemplatesByArenaDocument,
  useDataPackageExportLazyQuery,
  useDeleteDataPackageMutation,
  useGetDataPackageQuery,
  useRestoreDataPackageMutation,
  useUpdateDataPackageMetadataMutation,
} from "~/graphql/supergraphHooks";
import { safeRefetch } from "./safeRefetch";

type SaveMetadataInput = {
  name: string;
  description: string;
};

/** Bundles the data-package read + slideout mutations. Each handler shows
 * its own toast and resolves to `true`/`false` so callers can react with a
 * single boolean check. */
export const useDataPackageMutations = ({
  arenaId,
  dataPackageId,
  onDataPackageChanged,
}: {
  arenaId: number | null;
  dataPackageId: number | null;
  onDataPackageChanged: () => void;
}) => {
  const apolloClient = useApolloClient();
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  const { data, loading, refetch } = useGetDataPackageQuery({
    variables: { arenaId, dataPackageId: dataPackageId! },
    skip: !dataPackageId,
    fetchPolicy: "cache-and-network",
  });

  const [updateMetadata] = useUpdateDataPackageMetadataMutation();
  const [deleteDataPackageMutation, { loading: isArchiving }] = useDeleteDataPackageMutation();
  const [exportDataPackage, { loading: isExporting }] = useDataPackageExportLazyQuery({
    fetchPolicy: "no-cache",
  });
  const [restoreDataPackageMutation, { loading: isRestoring }] =
    useRestoreDataPackageMutation();

  const saveMetadata = useCallback(
    async (values: SaveMetadataInput): Promise<boolean> => {
      if (!dataPackageId) return false;
      setIsSavingSettings(true);
      try {
        const { data: result } = await updateMetadata({
          variables: { dataPackageId, arenaId, ...values },
        });
        const mutationResult = result?.finpubDataPackageUpdateMetadata;
        if (!mutationResult?.success) {
          toast.error(mutationResult?.message ?? "Failed to update settings.");
          return false;
        }
        toast.success("Data package settings updated");
        await safeRefetch(refetch, "data package");
        // Templates list shows the package name + desc — keep it in sync.
        await apolloClient.refetchQueries({
          include: [GetReportTemplatesByArenaDocument],
        });
        onDataPackageChanged();
        return true;
      } catch {
        toast.error("Failed to update settings. Please try again.");
        return false;
      } finally {
        setIsSavingSettings(false);
      }
    },
    [arenaId, apolloClient, dataPackageId, onDataPackageChanged, refetch, updateMetadata],
  );

  const archive = useCallback(async (): Promise<boolean> => {
    if (!dataPackageId) return false;
    try {
      await deleteDataPackageMutation({
        variables: { arenaId, dataPackageId },
        refetchQueries: [GetFormulasDocument, GetQueriesDocument],
      });
      await safeRefetch(refetch, "data package");
      toast.success("Data package has been archived");
      return true;
    } catch {
      toast.error("Failed to archive data package");
      return false;
    }
  }, [arenaId, dataPackageId, deleteDataPackageMutation, refetch]);

  const restore = useCallback(async (): Promise<boolean> => {
    if (!dataPackageId) return false;
    try {
      const { data: result } = await restoreDataPackageMutation({
        variables: { arenaId, dataPackageId },
        refetchQueries: [GetFormulasDocument, GetQueriesDocument],
      });
      const mutationResult = result?.finpubDataPackageRestore;
      if (!mutationResult?.success) {
        toast.error(mutationResult?.message ?? "Failed to restore data package");
        return false;
      }
      toast.success("Data package has been restored", { position: "bottom-center" });
      await safeRefetch(refetch, "data package");
      onDataPackageChanged();
      return true;
    } catch {
      toast.error("Failed to restore data package");
      return false;
    }
  }, [arenaId, dataPackageId, onDataPackageChanged, refetch, restoreDataPackageMutation]);

  const download = useCallback(
    async (version?: number): Promise<void> => {
      if (!dataPackageId) return;
      try {
        const { data: exportData } = await exportDataPackage({
          variables: { arenaId, dataPackageId, version },
        });
        const url = exportData?.finpubDataPackageExport?.preSignedUrl;
        if (url) {
          window.open(url, "_blank", "noopener,noreferrer");
        } else {
          toast.error("Failed to generate download link");
        }
      } catch {
        toast.error("Failed to export data package");
      }
    },
    [arenaId, dataPackageId, exportDataPackage],
  );

  return {
    dataPackage: data?.finpubDataPackage,
    isDataPackageLoading: loading,
    refetchDataPackage: refetch,
    saveMetadata,
    isSavingSettings,
    archive,
    isArchiving,
    restore,
    isRestoring,
    download,
    isExporting,
  };
};
